import './style.scss'
import React, { useRef, useState } from 'react'
import { RiCloseFill, RiDownloadLine, RiLoader4Line } from 'react-icons/ri'
import uploadIconDefault from 'src/assets/v2/img/upload_icon.svg'

export interface UploadedFile {
    name: React.ReactNode
    size?: React.ReactNode
    icon?: React.ReactNode
    file?: File
    url?: string
}

export interface FileUploadProps {
    value?: UploadedFile | null
    onSelect?: (file: File) => void
    onRemove?: () => void
    uploadIcon?: React.ReactNode
    fileIcon?: React.ReactNode
    downloadIcon?: React.ReactNode
    label?: React.ReactNode
    hint?: React.ReactNode
    accept?: string
    height?: string
    className?: string
}

function triggerDownload(href: string, filename: string) {
    const a = document.createElement('a')
    a.href = href
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
}

function filenameFromUrl(url: string, fallback: string) {
    try {
        const path = new URL(url).pathname
        const last = decodeURIComponent(path.substring(path.lastIndexOf('/') + 1))
        return last || fallback
    } catch {
        return fallback
    }
}

export const FileUpload: React.FC<FileUploadProps> = ({
                                                          value, onSelect, onRemove, uploadIcon, fileIcon, downloadIcon,
                                                          label = 'Click to upload', hint, accept, height = '11', className = '',
                                                      }) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [downloading, setDownloading] = useState(false)

    const pick = () => inputRef.current?.click()
    const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0]
        if (f) onSelect?.(f)
        e.target.value = ''
    }

    const handleDownload = async () => {
        if (!value || downloading) return
        const fallbackName = typeof value.name === 'string' ? value.name : 'download'

        // Locally-picked file, not yet on the server — download straight
        // from the in-memory File, no network round trip needed.
        if (value.file) {
            const blobUrl = URL.createObjectURL(value.file)
            triggerDownload(blobUrl, fallbackName)
            URL.revokeObjectURL(blobUrl)
            return
        }

        // Stored institution asset — fetch it as a blob so the browser is
        // forced to download rather than navigate to it.
        if (value.url) {
            setDownloading(true)
            try {
                const res = await fetch(value.url, { mode: 'cors' })
                const blob = await res.blob()
                const blobUrl = URL.createObjectURL(blob)
                triggerDownload(blobUrl, filenameFromUrl(value.url, fallbackName))
                URL.revokeObjectURL(blobUrl)
            } catch {
                window.open(value.url, '_blank', 'noopener')
            } finally {
                setDownloading(false)
            }
        }
    }

    return (
        <div className={`upload ${className}`} style={{ minHeight: `${height}rem` }}>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="upload__native"
                onChange={handle}
            />

            {value ? (
                <div className="upload__preview">
                    <div className="upload__file">
                        {(value.icon ?? fileIcon) && (
                            <span className="upload__fileicon">{value.icon ?? fileIcon}</span>
                        )}
                        <div className="upload__meta">
                            <span className="upload__name">{value.name}</span>
                            {value.size && <span className="upload__size">{value.size}</span>}
                        </div>
                    </div>

                    {(value.file || value.url) && (
                        <button
                            type="button"
                            className="upload__download"
                            onClick={handleDownload}
                            disabled={downloading}
                            aria-label="Download file"
                            title="Download"
                        >
                            {downloadIcon ?? (
                                downloading
                                    ? <RiLoader4Line className="upload__spin" />
                                    : <RiDownloadLine />
                            )}
                        </button>
                    )}

                    <button
                        type="button"
                        className="upload__remove"
                        onClick={onRemove}
                        aria-label="Remove file"
                    >
                        <RiCloseFill />
                    </button>
                </div>
            ) : (
                <button type="button" className="upload__dropzone" onClick={pick}>
                    <span className="upload__uploadicon">
                        {uploadIcon ?? <img src={uploadIconDefault} alt="" />}
                    </span>
                    <span className="upload__label">{label}</span>
                    {hint && <span className="upload__hint">{hint}</span>}
                </button>
            )}
        </div>
    )
}

export default FileUpload